<script setup lang="ts">
import { Check, ChevronDown, Search, X } from "lucide-vue-next";

type MultiSelectOption = {
  id: number;
  label: string;
};

const {
  options,
  selectedIds,
  placeholder,
  emptyText,
  selectedText = "",
  searchPlaceholder = "",
  sheetTitle = "",
  showChips = true,
  triggerClass = "",
  activeTriggerClass = "",
  inactiveTriggerClass = "",
  clearButtonClass = "",
  applyText = "Apply",
} = defineProps<{
  options: MultiSelectOption[];
  selectedIds: number[];
  placeholder: string;
  emptyText: string;
  selectedText?: string;
  searchPlaceholder?: string;
  sheetTitle?: string;
  showChips?: boolean;
  triggerClass?: string;
  activeTriggerClass?: string;
  inactiveTriggerClass?: string;
  clearButtonClass?: string;
  applyText?: string;
}>();

const emit = defineEmits<{
  apply: [ids: number[]];
  clear: [];
}>();

const query = ref("");
const draftIds = ref<number[]>([]);

const selectedSet = computed(() => new Set(selectedIds));
const draftSet = computed(() => new Set(draftIds.value));

const selectedOptions = computed(() => {
  return options.filter(option => selectedSet.value.has(option.id));
});

const filteredOptions = computed(() => {
  const value = query.value.trim().toLowerCase();

  if (!value) return options;

  return options.filter(option => option.label.toLowerCase().includes(value));
});

const triggerText = computed(() => {
  if (selectedText) return selectedText;

  if (!selectedOptions.value.length) return placeholder;

  if (selectedOptions.value.length === 1) {
    return selectedOptions.value[0]?.label || placeholder;
  }

  return `${selectedOptions.value.length} selected`;
});

const searchPlaceholderText = computed(() => searchPlaceholder || placeholder);

function syncDraft() {
  draftIds.value = [...selectedIds];
  query.value = "";
}

function toggleOption(option: MultiSelectOption) {
  const next = new Set(draftIds.value);

  if (next.has(option.id)) {
    next.delete(option.id);
  } else {
    next.add(option.id);
  }

  draftIds.value = Array.from(next);
}

function apply(close: () => void) {
  emit("apply", [...draftIds.value]);
  close();
}

watch(
  () => selectedIds,
  value => {
    draftIds.value = [...value];
  },
  { immediate: true, deep: true },
);
</script>

<template>
  <div class="w-full min-w-0">
    <AppDropdown
      class="w-full"
      content-class="left-0 right-auto !w-[300px] !min-w-[300px] max-w-[calc(100vw-32px)] !p-0"
      sheet-class="max-h-[76dvh] !p-0"
    >
      <template #trigger="{ open }">
        <button
          type="button"
          class="flex h-10 w-full min-w-0 items-center justify-between gap-3 rounded-lg border bg-white px-3 text-left text-sm font-semibold transition duration-300"
          :class="[
            triggerClass,
            open || selectedOptions.length
              ? activeTriggerClass ||
                'border-primary bg-primary-50/40 text-primary hover:bg-primary-50'
              : inactiveTriggerClass ||
                'border-app-border text-app-text hover:bg-gray-50',
          ]"
          @click="!open && syncDraft()"
        >
          <span
            class="min-w-0 flex-1 truncate"
            :class="
              open || selectedOptions.length ? 'text-primary' : 'text-app-text'
            "
          >
            {{ triggerText }}
          </span>

          <span
            v-if="selectedOptions.length"
            role="button"
            tabindex="0"
            class="flex size-5 shrink-0 items-center justify-center rounded-full transition duration-300"
            :class="
              clearButtonClass || 'bg-gray-400 text-white hover:bg-gray-500'
            "
            @click.stop="emit('clear')"
            @keydown.enter.stop.prevent="emit('clear')"
            @keydown.space.stop.prevent="emit('clear')"
          >
            <X class="size-3.5" />
          </span>

          <ChevronDown
            v-else
            class="size-4 shrink-0 transition duration-300"
            :class="open ? 'rotate-180 text-primary' : 'text-app-muted'"
          />
        </button>
      </template>

      <template #content="{ close }">
        <div
          class="flex max-h-[380px] min-h-[250px] w-full min-w-0 flex-col bg-white"
        >
          <label
            class="flex h-12 w-full min-w-0 shrink-0 items-center gap-2 border-b border-app-border px-4 focus-within:border-primary"
          >
            <Search class="size-4 shrink-0 text-app-muted" />
            <input
              v-model="query"
              type="text"
              :placeholder="searchPlaceholderText"
              class="min-w-0 flex-1 border-0 bg-transparent text-sm text-app-text outline-none placeholder:text-app-placeholder"
              @click.stop
            />
          </label>

          <ul
            class="app-multiselect-scroll flex min-h-0 flex-1 flex-col divide-y divide-app-border overflow-y-auto overscroll-contain"
            data-scroll-lock-ignore
          >
            <li v-for="option in filteredOptions" :key="option.id">
              <button
                type="button"
                class="flex min-h-12 w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-semibold text-app-text transition duration-300 hover:bg-primary-50"
                @click.stop="toggleOption(option)"
              >
                <span class="truncate">{{ option.label }}</span>

                <span
                  class="flex size-5 shrink-0 items-center justify-center rounded-[5px] border transition duration-300"
                  :class="
                    draftSet.has(option.id)
                      ? 'border-primary bg-primary text-white'
                      : 'border-[#7B8389] bg-white text-transparent'
                  "
                  aria-hidden="true"
                >
                  <Check class="size-3.5" stroke-width="2.5" />
                </span>
              </button>
            </li>

            <li
              v-if="!filteredOptions.length"
              class="flex flex-1 items-center justify-center px-4 py-4 text-center text-sm text-app-muted"
            >
              {{ emptyText }}
            </li>
          </ul>

          <div class="shrink-0 border-t border-app-border bg-white p-3">
            <button
              type="button"
              class="flex h-11 w-full items-center justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-white transition duration-300 hover:bg-primary-400"
              @click.stop="apply(close)"
            >
              {{ applyText }}
            </button>
          </div>
        </div>
      </template>

      <template #mobile-content="{ close }">
        <div class="flex max-h-[76dvh] min-h-[420px] flex-col bg-white">
          <div class="shrink-0 border-b border-app-border px-4 pb-3 pt-4">
            <h3 class="text-lg font-semibold text-app-text">
              {{ sheetTitle || placeholder }}
            </h3>
          </div>

          <label
            class="flex h-12 w-full min-w-0 shrink-0 items-center gap-2 border-b border-app-border px-4"
          >
            <Search class="size-4 shrink-0 text-app-muted" />
            <input
              v-model="query"
              type="text"
              :placeholder="searchPlaceholderText"
              class="min-w-0 flex-1 border-0 bg-transparent text-base text-app-text outline-none placeholder:text-app-placeholder"
              @click.stop
            />
          </label>

          <ul
            class="app-multiselect-scroll flex min-h-0 flex-1 flex-col divide-y divide-app-border overflow-y-auto"
          >
            <li v-for="option in filteredOptions" :key="option.id">
              <button
                type="button"
                class="flex min-h-14 w-full items-center justify-between gap-3 px-4 py-3.5 text-left text-base font-semibold text-app-text transition duration-300 active:bg-primary-50"
                @click.stop="toggleOption(option)"
              >
                <span class="truncate">{{ option.label }}</span>

                <span
                  class="flex size-6 shrink-0 items-center justify-center rounded-[6px] border transition duration-300"
                  :class="
                    draftSet.has(option.id)
                      ? 'border-primary bg-primary text-white'
                      : 'border-[#7B8389] bg-white text-transparent'
                  "
                  aria-hidden="true"
                >
                  <Check class="size-4" stroke-width="2.5" />
                </span>
              </button>
            </li>

            <li
              v-if="!filteredOptions.length"
              class="flex flex-1 items-center justify-center px-4 py-5 text-center text-base text-app-muted"
            >
              {{ emptyText }}
            </li>
          </ul>

          <div class="shrink-0 border-t border-app-border bg-white p-3">
            <button
              type="button"
              class="flex h-12 w-full items-center justify-center rounded-lg bg-primary text-base font-bold text-white transition duration-300 active:bg-primary-400"
              @click.stop="apply(close)"
            >
              {{ applyText }}
            </button>
          </div>
        </div>
      </template>
    </AppDropdown>

    <div
      v-if="showChips && selectedOptions.length"
      class="mt-3 flex flex-wrap gap-2"
    >
      <button
        v-for="option in selectedOptions"
        :key="option.id"
        type="button"
        class="inline-flex max-w-full items-center gap-1.5 rounded-lg bg-gray-100 px-3 py-1.5 text-sm font-semibold text-app-text transition duration-300 hover:bg-gray-200"
        @click="
          emit(
            'apply',
            selectedIds.filter(id => id !== option.id),
          )
        "
      >
        <span class="truncate">{{ option.label }}</span>
        <X class="size-3.5 shrink-0 text-app-muted" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.app-multiselect-scroll {
  scrollbar-width: thin;
  scrollbar-color: #aeb8bf #f4f6f7;
  scrollbar-gutter: stable;
}

.app-multiselect-scroll::-webkit-scrollbar {
  height: 8px;
  width: 9px;
}

.app-multiselect-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  border: 2px solid #f4f6f7;
  background: #aeb8bf;
}

.app-multiselect-scroll::-webkit-scrollbar-track {
  background: #f4f6f7;
}
</style>
